import {
  BUY_TOOL_NAME,
  GOVERNED_AGENTS,
  GovernedAgent,
  INTAKE_TOOL_NAMES,
  PLAN_TOOL_NAMES
} from '@blisspointmedia/bpm-types';
import { AgentResponseSegment, AgentToolCallSegment } from '../../utils/agent-client';
import { PendingAgentOrder } from '../AgentOrderApproval/agent-order-approval-constants';

// Orchestration trace: the four governed sub-agents of the planning->buying flow
// and their live handoff status. The identities/scopes/owner mirror the agent
// registry ENFORCED IN CODE (tinuiti-tvad-mediaplanning-agent
// app/agent/governance/registry.py `_build_default_registry`), where each stage
// dispatches through a governed step that require-scopes the identity and audits
// the invocation. The *status* here is derived from real signals — the ordered
// tool calls the agent actually made (same source DecisionTrailPanel trusts) plus
// live order state — never authored. If a signal is absent the stage simply reads
// as pending rather than inventing progress.

export const TraceStatus = {
  DONE: 'done',
  ACTIVE: 'active',
  PENDING: 'pending'
} as const;
export type TraceStatusType = (typeof TraceStatus)[keyof typeof TraceStatus];

export interface TraceStage extends GovernedAgent {
  status: TraceStatusType;
  detail: string;
}

// The governed agents, their scopes/owner, and the tool-name -> stage mapping all
// come from the shared bpm-types definition (GOVERNED_AGENTS, *_TOOL_NAMES), which
// mirrors the Python runtime registry. Derive lookup Sets from the shared arrays so
// the trace stays in lockstep with that one source of truth.
const INTAKE_TOOLS = new Set<string>(INTAKE_TOOL_NAMES);
const PLAN_TOOLS = new Set<string>(PLAN_TOOL_NAMES);
const BUY_TOOL = BUY_TOOL_NAME;

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
const arr = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

const toolCalls = (segments: AgentResponseSegment[]): AgentToolCallSegment[] =>
  segments.filter((segment): segment is AgentToolCallSegment => segment.type === 'tool_call');

interface TraceInputs {
  segments: AgentResponseSegment[];
  newtonReplayed: boolean;
  orders: PendingAgentOrder[] | undefined;
}

// Networks + line-item count from the plan-submission tool call, for the planner
// stage's live detail line. Defensive: returns undefined on any shape drift.
const planDetail = (calls: AgentToolCallSegment[]): string | undefined => {
  const plan = calls.find(c => PLAN_TOOLS.has(c.name));
  if (!plan) return undefined;
  const lines = arr(asRecord(plan.args).line_items).map(asRecord);
  if (lines.length === 0) return undefined;
  const networks = new Set(
    lines.map(l => String(l.network_name || l.network || '')).filter(Boolean)
  );
  const lineLabel = lines.length === 1 ? 'line' : 'lines';
  const netLabel = networks.size === 1 ? 'network' : 'networks';
  return `Submitted plan: ${lines.length} ${lineLabel} across ${networks.size} ${netLabel}.`;
};

export const deriveOrchestrationTrace = ({
  segments,
  newtonReplayed,
  orders
}: TraceInputs): TraceStage[] => {
  const calls = toolCalls(segments);
  const names = new Set(calls.map(c => c.name));
  const hasPlan = [...PLAN_TOOLS].some(name => names.has(name));
  const hasIntake = hasPlan || [...INTAKE_TOOLS].some(name => names.has(name));
  const orderCount = orders?.length ?? 0;
  const hasBuy = names.has(BUY_TOOL) || orderCount > 0;

  const statusFor = (id: string): TraceStatusType => {
    if (id === 'newton-preflight') {
      return newtonReplayed ? TraceStatus.DONE : TraceStatus.PENDING;
    }
    if (id === 'intake') {
      if (hasIntake) return TraceStatus.DONE;
      return newtonReplayed ? TraceStatus.ACTIVE : TraceStatus.PENDING;
    }
    if (id === 'streaming-planner') {
      if (hasPlan) return TraceStatus.DONE;
      return hasIntake ? TraceStatus.ACTIVE : TraceStatus.PENDING;
    }
    // buying
    if (hasBuy) return TraceStatus.DONE;
    return hasPlan ? TraceStatus.ACTIVE : TraceStatus.PENDING;
  };

  const detailFor = (id: string): string => {
    if (id === 'newton-preflight') {
      return 'Ran the curated Bliss Point Audience & Planner blueprints and built the client context pack.';
    }
    if (id === 'intake') {
      return 'Pre-filled the intake from the Bliss Point Audience & Planner pre-flight and confirmed it at Gate 1.';
    }
    if (id === 'streaming-planner') {
      return planDetail(calls) ?? 'Builds the rate-card-anchored line-item plan.';
    }
    // buying
    if (orderCount > 0) {
      const label = orderCount === 1 ? 'order' : 'orders';
      return `Drafted ${orderCount} insertion ${label}; held for human approval — no send.`;
    }
    return 'Turns approved direct-IO lines into DRAFT insertion orders, held for approval.';
  };

  return GOVERNED_AGENTS.map(agent => ({
    ...agent,
    status: statusFor(agent.id),
    detail: detailFor(agent.id)
  }));
};
